import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface EnterpriseStore {
  /** Whether the sidebar is collapsed to icon-only mode. */
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;

  /**
   * The last event the user actually looked at, so an event-less route (Chat,
   * the org pages) can still say which event "Proposals" would mean.
   *
   * Deliberately not persisted: it describes where you are in this tab, not a
   * preference, and a remembered event surviving a reload into a different org
   * is exactly the bug the membership check below guards against.
   */
  lastEventId: string | null;
  /** Pass null to forget it, which is how leaving for an org route is recorded. */
  setLastEventId: (eventId: string | null) => void;

  /** SSR hydration safety flag. */
  hasHydrated: boolean;
  setHasHydrated: (hydrated: boolean) => void;
}

/**
 * Zustand store for enterprise sidebar UI state.
 *
 * Only `isSidebarCollapsed` is persisted (see `partialize`); `lastEventId` is
 * per-tab by design.
 */
export const useEnterpriseStore = create<EnterpriseStore>()(
  persist(
    (set) => ({
      isSidebarCollapsed: false,
      setIsSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

      lastEventId: null,
      setLastEventId: (lastEventId) => set({ lastEventId }),

      hasHydrated: false,
      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: "enterprise-sidebar",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
